/**
 * COCHRANE-FOOTHILLS — roster wave entries. GENERATED BY ASSEMBLY, WRITTEN BY REVIEW.
 *
 * Geography (streets, landmarks, coordinates, adjacency) ported from the source
 * roster; airspace derived by the calibrated classifier in
 * scripts/derive-airspace.mjs; every descriptive sentence written fresh from
 * aerial-media truth and fact-checked against its generation brief. See
 * docs/geo/VISION-v3.md for the fact contract these entries were written under.
 *
 * 5 entries. Edit by hand freely — assembly never overwrites a
 * file that has drifted from its fragments without --force.
 */
import type { Location } from "../types";

export const ROSTER_COCHRANE_FOOTHILLS: Location[] = [
// ─────────────────────────────────────────────────────────────── CREMONA ──
  {
    slug: "cremona",
    name: "Cremona",
    region: "cochrane-foothills",
    city: "Cremona",
    province: "Alberta",
    coordinates: { lat: 51.5489, lng: -114.4853 },
    tier: 2,
    driveMinutesFromCalgary: 60,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Sundre aerodrome",
      distanceToAerodromeKm: 29,
      authorizationRequired: false,
      authorizationNote:
        "Cremona lies outside the charted Calgary-area control zones in uncontrolled Class G airspace, about 29 km from Sundre aerodrome. No airspace authorisation is normally required. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "The ground west of Cremona climbs toward Bergen Rocks, so the village loses direct evening sun to a raised horizon while the grain land east along Highway 580 is still lit.",
      consequence:
        "A shoot scheduled off a Calgary sunset table arrives to find the warm light already gone at ground level, and the frames meant to sell the foothill setting go flat.",
      resolution:
        "The golden-hour hold is planned against Cremona's own horizon rather than the almanac — flown in the window that actually exists and delivered in the standard turnaround.",
    },
    landmarks: [
      "Highway 22",
      "Highway 580",
      "Main Street",
      "Cremona Community Centre",
      "Bergen Rocks",
    ],
    terrain:
      "A village at the seam between cultivated prairie and the first rise of the foothills — flat, worked ground to the east and rolling, treed country climbing west toward Bergen Rocks.",
    windNote:
      "Chinook belt. West wind comes off the foothills with the gust spread already built in, and the open ground along Highway 22 gives it nothing to slow against.",
    seasonalWindow:
      "Evening light gives out early once the sun drops behind the rising ground west of the village; mornings, lit from over the flat grain land, are the more predictable end of the day. Snow holds on the treed western rises after the fields have cleared.",
    bylawNote:
      "Village of Cremona park and pathway rules govern launch and landing on municipal land; takeoff from private property with the owner's consent is the default.",
    subjectMix: ["luxury-residential", "new-build-subdivision", "commercial-retail"],
    mobilisationNote:
      "About an hour northwest of the Calgary base via Highway 22. Same-day is possible with a morning confirmation, and Water Valley or Dog Pound can share the same mobilisation.",
    faqs: [
      {
        question: "Is Cremona inside any controlled airspace?",
        answer:
          "No. Cremona sits in uncontrolled Class G airspace, outside the charted Calgary-area control zones, with the nearest aerodrome at Sundre about 29 km away. No airspace authorisation is normally required to fly here. The specific site and altitude are still confirmed against NAV Drone before every flight.",
      },
      {
        question: "When is the best light for an aerial shoot in Cremona?",
        answer:
          "Morning, or the hour before the sun reaches the foothill horizon. The ground climbs west of the village toward Bergen Rocks, so direct evening light ends earlier than sunset tables suggest. Booking against that earlier cutoff keeps the golden-hour frames on the schedule instead of off it.",
      },
      {
        question: "Can one booking cover Cremona and a nearby property?",
        answer:
          "Yes. Cremona, Water Valley and Dog Pound sit on the same run from Calgary, so two sites can share one mobilisation. The hour's drive from the base means the practical approach is a half-day window, with the order of sites set by the light.",
      },
    ],
    primaryKeywords: [
      "drone photography Cremona",
      "aerial photography Cremona",
      "Cremona drone services",
    ],
    nearestLocations: ["water-valley", "madden", "cochrane", "dog-pound"],
  },

  // ───────────────────────────────────────────────────────────── DOG POUND ──
  {
    slug: "dog-pound",
    name: "Dog Pound",
    region: "cochrane-foothills",
    city: "Dog Pound",
    province: "Alberta",
    coordinates: { lat: 51.4833, lng: -114.4333 },
    tier: 3,
    driveMinutesFromCalgary: 50,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Olds-Didsbury aerodrome",
      distanceToAerodromeKm: 34,
      authorizationRequired: false,
      authorizationNote:
        "In uncontrolled Class G airspace about 34 km from Olds-Didsbury aerodrome, Dog Pound lies outside the charted Calgary-area control zones, and no authorisation is normally required. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "From the air, Dog Pound is a crossroads rather than a town: Highway 22 runs straight through the survey grid while Dog Pound Creek crosses it on a line of its own.",
      consequence:
        "With no streetscape to borrow, a ground-level photograph strands an acreage in anonymous countryside — nothing in frame says where the property is or how it is reached.",
      resolution:
        "An approach run along Highway 22 into a boundary-line orbit puts the property, its access and the creek line into one readable frame, delivered in the standard turnaround.",
    },
    landmarks: [
      "Highway 22",
      "Dog Pound Creek",
      "Big Hill Springs Provincial Park",
      "Horse Creek Road",
    ],
    terrain:
      "Open ranch and crop land laid out on the survey grid, with Dog Pound Creek cutting a shallow, winding valley across it — the only line in the landscape that ignores the roads.",
    windNote:
      "Chinook country, and open. West and southwest winds cross this stretch with little in their way; the shallow Dog Pound Creek line offers brief shelter, not protection.",
    seasonalWindow:
      "Green-up runs first along Dog Pound Creek and later on the open upland, so spring frames read best near the water. In winter the creek line is often the only dark feature in an otherwise white grid.",
    bylawNote:
      "Local municipal rules govern launches from public land, and Big Hill Springs Provincial Park is not treated as a launch site; takeoff from private property with the owner's consent is the default.",
    subjectMix: ["luxury-residential", "commercial-retail"],
    mobilisationNote:
      "Roughly 50 minutes up Highway 22 from the Calgary base. An easy half-day booking, and Cremona or Madden can share the same trip.",
    faqs: [
      {
        question: "What airspace rules apply to a drone flight at Dog Pound?",
        answer:
          "Dog Pound is in uncontrolled Class G airspace, well clear of the Calgary-area control zones — the nearest aerodrome is Olds-Didsbury, about 34 km away. No airspace authorisation is normally needed. Every booking is still checked against NAV Drone for the exact site before the flight.",
      },
      {
        question: "How do you photograph an acreage that has no town around it?",
        answer:
          "By using the roads and the creek. From the air a Dog Pound property is located by its relationship to Highway 22, the range-road grid and Dog Pound Creek, so the orbit is planned to hold at least one of those references in frame while keeping the buildings large.",
      },
    ],
    primaryKeywords: [
      "drone photography Dog Pound",
      "aerial photography Dog Pound",
      "Dog Pound drone services",
    ],
    nearestLocations: ["cremona", "madden", "water-valley", "cochrane"],
  },

  // ──────────────────────────────────────────────────────────────── MADDEN ──
  {
    slug: "madden",
    name: "Madden",
    region: "cochrane-foothills",
    city: "Madden",
    province: "Alberta",
    coordinates: { lat: 51.45, lng: -114.25 },
    tier: 3,
    driveMinutesFromCalgary: 45,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Airdrie aerodrome",
      distanceToAerodromeKm: 30,
      authorizationRequired: false,
      authorizationNote:
        "In uncontrolled Class G airspace about 30 km from Airdrie aerodrome, Madden lies outside the charted Calgary-area control zones, and no authorisation is normally required. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "The quarters around Madden run a visible calendar — seeded rows, standing crop, swath lines off Highway 791, then stubble under the first snow — and each stage holds for only a few weeks.",
      consequence:
        "Shot in the wrong week, the same acreage reads as bare dirt instead of working land, and no camera setting recovers a stage the season has already closed.",
      resolution:
        "Shoots here are booked to the land's stage as much as the client's diary, and where the story is change itself, a progress series covers it — each pass delivered in the standard turnaround.",
    },
    landmarks: [
      "Highway 791",
      "Madden General Store",
      "Madden Community Hall",
      "Symons Valley Road",
    ],
    terrain:
      "Gently rolling grain and hay country northwest of Calgary — long open quarters where shelterbelts and yard sites are the only vertical interruptions.",
    windNote:
      "Chinook belt, with no terrain to blunt it. Wind that reads as a breeze in a sheltered Calgary yard runs at full strength across the open quarters here.",
    seasonalWindow:
      "Long June evenings put low, raking light across field texture, and the weeks either side of harvest give the strongest colour the district gets. Winter flattens the open quarters to white within a day of the first real snowfall.",
    bylawNote:
      "Launches from public land around Madden fall under local municipal rules; the working default is takeoff and landing from the client's own property with consent.",
    subjectMix: ["luxury-residential", "new-build-subdivision", "commercial-retail"],
    mobilisationNote:
      "About 45 minutes from the Calgary base via Symons Valley Road. Comfortable as a same-day booking, and close enough to Dog Pound and Cremona to stack two shoots in one window of light.",
    faqs: [
      {
        question: "Do you need airspace authorisation to fly near Madden?",
        answer:
          "No. Madden sits in uncontrolled Class G airspace outside the charted Calgary-area control zones, about 30 km from Airdrie aerodrome, so no airspace authorisation is normally required. The specific site and altitude are confirmed against NAV Drone before every flight as standard practice.",
      },
      {
        question: "What time of year should a Madden acreage be photographed?",
        answer:
          "Match the shoot to what the land is doing. The quarters along Highway 791 move from seeded rows to standing crop to swath lines to stubble, and each stage lasts weeks. A listing reads best in full crop or fresh snow; a bare-field April frame sells it short.",
      },
    ],
    primaryKeywords: [
      "drone photography Madden",
      "aerial photography Madden",
      "Madden drone services",
    ],
    nearestLocations: ["dog-pound", "cremona", "cochrane", "water-valley"],
  },

  // ─────────────────────────────────────────────────────── REDWOOD MEADOWS ──
  {
    slug: "redwood-meadows",
    name: "Redwood Meadows",
    region: "cochrane-foothills",
    city: "Redwood Meadows",
    province: "Alberta",
    coordinates: { lat: 50.9489, lng: -114.5897 },
    tier: 2,
    driveMinutesFromCalgary: 40,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Springbank Airport (CYBW)",
      distanceToAerodromeKm: 23,
      authorizationRequired: false,
      authorizationNote:
        "Redwood Meadows sits in uncontrolled Class G airspace, clear of the charted Calgary-area control zones and about 23 km from Springbank Airport (CYBW). Authorisation is not normally required here. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Redwood Meadows sits in clear Class G air, 23 km from Springbank Airport and outside its control zone — yet the townsite itself stands on Tsuut'ina Nation land, so the real constraint here is on the ground, not in the sky.",
      consequence:
        "An operator who plans a takeoff from the roadside or the surrounding land is staging from ground they have no right to use — the flight would be legal and the launch would not.",
      resolution:
        "Launches are staged from the client's own property inside the townsite, the plan is confirmed against NAV Drone, and the shoot is delivered in the standard turnaround.",
    },
    landmarks: [
      "Manyhorses Drive",
      "Wolf Drive",
      "Redwood Meadows Golf & Country Club",
      "Elbow River",
      "Tsuut'ina Nation",
    ],
    terrain:
      "A forested townsite beside the Elbow River — mature spruce and pine over curving residential streets, fairways threaded between the lots, and the foothills rising a short distance west.",
    windNote:
      "Chinook winds cross the whole corridor, but the mature timber around the townsite takes the edge off surface gusts; above the canopy the wind runs at full strength.",
    seasonalWindow:
      "The conifer canopy keeps its colour through winter, so frames here hold depth after the open country east has gone flat. The Elbow River valley and the tall timber pull shadow across the townsite early on short days.",
    bylawNote:
      "The townsite administers its own public spaces and the surrounding land belongs to the Tsuut'ina Nation, so launches are staged from the client's own property with consent.",
    subjectMix: ["luxury-residential", "commercial-retail"],
    mobilisationNote:
      "About 40 minutes west of the Calgary base. A straightforward same-day booking, often paired with Bragg Creek or Elbow Valley work on the same run.",
    faqs: [
      {
        question: "Is the airspace over Redwood Meadows restricted?",
        answer:
          "No. The townsite sits in uncontrolled Class G airspace about 23 km from Springbank Airport, outside its control zone, so no airspace authorisation is normally required. The flight plan is still confirmed against NAV Drone before every shoot, because traffic thickens toward Springbank on the corridor east.",
      },
      {
        question: "Where can a drone launch from in Redwood Meadows?",
        answer:
          "From the client's own property, with consent. The townsite stands on Tsuut'ina Nation land beside the Elbow River, so neither the surrounding Nation land nor the public spaces are treated as free launch sites. Staging from the property being photographed keeps the question simple and the flight legal.",
      },
      {
        question: "Can golf-course frontage be shown without flying over the fairways?",
        answer:
          "Usually, yes. Lots here back onto the Redwood Meadows Golf & Country Club, and a boundary-line orbit flown from the property side can hold both the house and the fairway in frame. Where a shot genuinely needs course overflight, it is arranged with the club rather than assumed.",
      },
    ],
    primaryKeywords: [
      "drone photography Redwood Meadows",
      "aerial photography Redwood Meadows",
      "Redwood Meadows drone services",
    ],
    nearestLocations: ["bragg-creek", "cochrane", "elbow-valley", "springbank"],
  },

  // ────────────────────────────────────────────────────────── WATER VALLEY ──
  {
    slug: "water-valley",
    name: "Water Valley",
    region: "cochrane-foothills",
    city: "Water Valley",
    province: "Alberta",
    coordinates: { lat: 51.55, lng: -114.6333 },
    tier: 3,
    driveMinutesFromCalgary: 60,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Sundre aerodrome",
      distanceToAerodromeKm: 25,
      authorizationRequired: false,
      authorizationNote:
        "In uncontrolled Class G airspace about 25 km from Sundre aerodrome, Water Valley lies outside the charted Calgary-area control zones, and no authorisation is normally required. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "From above, Water Valley is a single cluster of rooflines strung along Highway 579 — a clearing in foothill forest that closes back in directly behind the lots.",
      consequence:
        "A ground camera standing in that clearing sees buildings and a treeline; it cannot show that the forest is the setting, and the setting is what a buyer out here is paying for.",
      resolution:
        "A rising reveal from street height to above the canopy turns the treeline into a frame — hamlet, timber and, at the right angle, Winchell Lake in one shot, delivered in the standard turnaround.",
    },
    landmarks: [
      "Highway 579",
      "Bergen Road",
      "Water Valley Golf & Country Club",
      "Winchell Lake",
    ],
    terrain:
      "Rolling, heavily treed foothill country — small clearings, pasture pockets and lake basins cut out of mixed forest, closer in character to the mountains than to the prairie east of it.",
    windNote:
      "Still the chinook belt, though the treed, rolling ground breaks up surface wind more than the open grain country to the east; above the canopy it blows unbroken.",
    seasonalWindow:
      "Snow sits in the timber and on shaded ground here after Calgary has cleared, and autumn turns the deciduous stands around the Water Valley Golf & Country Club gold for a short window — the strongest colour the hamlet shows all year.",
    bylawNote:
      "Local municipal rules apply to launches from public land in the hamlet; takeoff and landing from private property with the owner's consent is the standard approach.",
    subjectMix: ["luxury-residential", "lakefront", "commercial-retail"],
    mobilisationNote:
      "About an hour from the Calgary base. Best booked as a half-day; Cremona and Dog Pound sit on the same run and can share the mobilisation.",
    faqs: [
      {
        question: "Are there flight restrictions over Water Valley?",
        answer:
          "None beyond the standard rules. Water Valley lies in uncontrolled Class G airspace, roughly 25 km from Sundre aerodrome and outside every charted Calgary-area control zone, so no airspace authorisation is normally required. The exact site is verified against NAV Drone before the flight, as it is everywhere.",
      },
      {
        question: "What does aerial photography show at Water Valley that ground photos miss?",
        answer:
          "The forest edge. Most properties here are clearings in treed foothill country, and the value is the timber around them — something a ground camera can only hint at. From altitude, one frame holds the buildings, the surrounding stands and, on the right side of the hamlet, Winchell Lake.",
      },
    ],
    primaryKeywords: [
      "drone photography Water Valley",
      "aerial photography Water Valley",
      "Water Valley drone services",
    ],
    nearestLocations: ["cremona", "madden", "dog-pound", "cochrane"],
  }
];
